import { Button, Input, message } from 'antd';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import GoogleIcon from '@/assets/icon_google.svg';
import MailIcon from '@/assets/icon_mail.svg';
import { ConfirmModal } from '@/components/ConfirmModal';
import { OAUTH_ADMIN_RESET_SECRET_EMAIL_FROM } from '@/constants/api-source-types';
import { useI18nContext } from '@/i18n/I18n';
import { resetMerchantSecret } from '@/services/oauthAdmin';
import { sendEmailPrivate } from '@/services/security';
import { useStore } from '@/store/useStore';
import { useUserStore } from '@/store/useUser';

type ResetSecretResult = {
  success: boolean;
  secret: string;
};
interface IResetSecretModal {
  showResetModal: boolean;
  closeModal: () => void;
  handleSecretModal: (t: ResetSecretResult) => void;
}
let intervalTimer: any = null;
export const ResetSecretModal: FC<IResetSecretModal> = ({
  showResetModal,
  closeModal,
  handleSecretModal,
}) => {
  const [$t] = useI18nContext();
  const { toggleUnBindGoogleVisible } = useStore();
  const {
    userProfile: { userInfo: user },
    wholeEmail,
    msgError,
  } = useUserStore();
  const [mailCode, setMailCode] = useState('');
  const [googleCode, setGoogleCode] = useState('');
  const [mailVerified, setMailVerified] = useState(false);
  const [isResendCode, setIsResendCode] = useState(false);
  const [second, setSecond] = useState(60);
  const [btnDisabled, setDisabled] = useState(false);
  const navigate = useNavigate();
  const targetToSecurityPage = () => {
    navigate('/app/user/security');
  };

  const _resetState = () => {
    setMailCode('');
    setGoogleCode('');
    setDisabled(false);
    setIsResendCode(false);
    if (intervalTimer) {
      clearInterval(intervalTimer);
      setSecond(60);
    }
  };
  const resendTime = () => {
    let resendTimer = 60;
    setDisabled(true);
    intervalTimer = setInterval(() => {
      resendTimer -= 1;
      if (resendTimer <= 0) {
        clearInterval(intervalTimer);
        resendTimer = 60;
        setSecond(60);
        setDisabled(false);
        setIsResendCode(true);
      } else {
        setSecond(resendTimer);
      }
    }, 1000);
  };

  const sendCodeToEmail = () => {
    // 开始倒计时60s
    resendTime();
    // 发送验证码到邮箱
    sendEmailPrivate({
      email: wholeEmail,
      from: OAUTH_ADMIN_RESET_SECRET_EMAIL_FROM,
    })
      .then(() => {
        setMailVerified(true);
      })
      .catch((error) => {
        const { ret_code } = error;
        if (ret_code === 10011) {
          message.error($t('requestTooFrequently'));
        } else {
          msgError(error);
        }
        setMailVerified(false);
      });
  };

  const resetSecret = () => {
    resetMerchantSecret({
      code_email: mailCode,
      code_2fa: googleCode,
      from: OAUTH_ADMIN_RESET_SECRET_EMAIL_FROM,
    })
      .then((res) => {
        if (res?.result?.client_secret) {
          // 重置成功
          handleSecretModal({
            success: true,
            secret: res?.result?.client_secret,
          });
          _resetState();
          closeModal();
        }
      })
      .catch((error) => {
        message.error(error?.ret_msg || 'server error');
      });
  };

  const openGAUnbind = (e: any) => {
    if (e) e.preventDefault();
    closeModal();
    toggleUnBindGoogleVisible();
  };

  const renderContent = () => {
    return (
      <div className="oauth-admin-reset-modal-content">
        <div className="mail-code-box">
          <div className="mail-code-box-title">
            <img src={MailIcon} alt="icon_mail" />
            {!user.email_verified ? (
              <span className="bind-email" onClick={targetToSecurityPage}>
                {$t('oauthAdmin.bindYourEmail')}
              </span>
            ) : (
              <span>
                {$t('VerificationCodeWillSendTo')}
                <b className="mail">{user.vague_email_v2}</b>
              </span>
            )}
          </div>
          <div className="mail-code-box-input">
            <Input
              maxLength={6}
              placeholder={$t('Mailbox verification code')}
              value={mailCode}
              onChange={(e) => {
                setMailCode(e.target.value);
              }}
              disabled={!user.email_verified}
            ></Input>
            <div className="send-code">
              {btnDisabled ? (
                <Button disabled type="link">
                  {second} s
                </Button>
              ) : (
                <Button
                  type="link"
                  onClick={sendCodeToEmail}
                  disabled={!user.email_verified}
                >
                  {isResendCode ? $t('Resend') : $t('Send verification code')}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="google-code-box">
          <div className="google-code-box-title">
            <img src={GoogleIcon} alt="icon_google" />
            {!user.has_google2fa ? (
              <span className="bind-g2fa" onClick={targetToSecurityPage}>
                {$t('To bind Google verification code')}
              </span>
            ) : (
              <span>{$t('Verify with Google')}</span>
            )}
          </div>
          <div className="mail-code-box-input">
            <Input
              placeholder={$t('Google verification code')}
              value={googleCode}
              onChange={(e) => {
                setGoogleCode(e.target.value);
              }}
              disabled={!user.has_google2fa}
            ></Input>
          </div>
          {user.has_google2fa && (
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <span className="textBtn" onClick={openGAUnbind}>
                {$t('googleVerificationNotAvailable')}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <ConfirmModal
      isVisible={showResetModal}
      title={$t('oauthAdmin.resetOauthSecret')}
      content={renderContent()}
      onCancel={() => {
        _resetState();
        closeModal();
      }}
      okBtnText={$t('Confirm')}
      confirmDisabled={googleCode === '' || mailCode === ''}
      onConfirm={() => {
        if (mailVerified) {
          resetSecret();
        } else {
          message.error('email verified error');
        }
      }}
    />
  );
};
